/* eslint-disable */

import {
  endDelegatedSession,
  getDelegatedSessionsById,
  getSessionEvents,
} from '@/api/oversight';
import { DelegatedSessions, SessionEvent } from '@/api/oversight/types';
import Snackbar from '@/components/Snackbar';
import { IconArrowForwardMS, IconCloseMS } from '@/constants/icons';
import { GlobalContext } from '@/context/global';
import {
  formatDateLongMonthLocal,
  formatMillisToMinutesSeconds,
} from '@/helpers';
import useErrorMessage from '@/hooks/useErrorMessage';
import useSnackbar from '@/hooks/useSnackbar';
import { Button } from '@/pages/Oversight/components/Button';
import { Timeline } from '@/pages/Oversight/components/Timeline';
import { WarningOutlined } from '@mui/icons-material';
import { Divider, IconButton, Tab, Tooltip } from '@mui/material';
import { ReactNode, useCallback, useContext, useEffect, useState } from 'react';

import { EndSessionConfirmDialog } from '@/pages/Oversight/components/EndSessionConfirmDialog';
import { SubtitleCell } from '@/pages/Oversight/components/Table/styles';
import { RoundedGreen } from '@/pages/Oversight/styles';
import { delegatedAccessLabels, formatDate } from '@/pages/Oversight/utils';
import '@sinm/react-chrome-tabs/css/chrome-tabs.css';
import { useParams } from 'react-router-dom';
import { expiredDetail } from '../CustomerDetails';
import { StyledTitle } from '../CustomerDetails/styles';
import {
  Container,
  ContainerInfo,
  DateTypography,
  DurationTypography,
  ErrorBox,
  Label,
  Span,
  TopContainer,
} from './styles';

type SectionClientProps = {
  id?: string;
  sessionId?: number;
  source?: 'delegated_access' | 'section_client' | 'section_adm_user';
  userRole?: string;
  error_count?: number;
  device?: string;
  started_at?: string | null;
  ended_at?: string | null;
  delegated_session_id?: number | null;
  onClose: () => void;
  onActivityClick?: (id: number) => void;
  onSessionEnded?: () => void;
};

type Tab = {
  id: string;
  title: string;
  active?: boolean;
};

export function SectionClient({
  id,
  sessionId,
  source,
  onClose,
  onActivityClick,
  onSessionEnded,
  userRole,
  error_count,
  device,
  started_at = null,
  ended_at = null,
  delegated_session_id = null,
}: SectionClientProps): JSX.Element {
  const [events, setEvents] = useState<SessionEvent[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [records, setRecords] = useState(0);
  const [, os, browser] = device?.split(' / ') || ['', '', ''];
  const [browserName, browserVersion] = browser.split(/ (.+)/);
  const sessionDurationMs =
    started_at && ended_at
      ? new Date(ended_at).getTime() - new Date(started_at).getTime()
      : null;
  const [delegatedSessions, setDelegatedSessions] = useState<DelegatedSessions>(
    undefined as unknown as DelegatedSessions
  );

  const [openEndSessionDialog, setOpenEndSessionDialog] = useState(false);
  const [endingSession, setEndingSession] = useState(false);

  const { handleSnackbar } = useSnackbar();
  const { getErrorMessage } = useErrorMessage();

  const getDelegatedSessionData = useCallback(
    async (delegatedSessionId: number) => {
      try {
        const { data, error } = await getDelegatedSessionsById(
          delegatedSessionId
        );

        if (error) throw new Error(error.message);
        if (!data) throw new Error('Algo deu errado, tente novamente.');

        setDelegatedSessions(data);
      } catch (error) {
        handleSnackbar(getErrorMessage(error), true);
      }
    },
    [handleSnackbar, getErrorMessage]
  );

  const fetchEvents = async (pageToLoad: number): Promise<void> => {
    if (!sessionId) return;

    try {
      const { data } = await getSessionEvents(sessionId, pageToLoad);
      if (data) {
        setEvents((prev) =>
          pageToLoad === 1
            ? data.session_events
            : [...prev, ...data.session_events]
        );

        setTotalPages(data.pages);
        setPage(data.current_page);
        setRecords(data.records);
      }
    } catch (error) {
      handleSnackbar(getErrorMessage(error), true);
    }
  };

  useEffect(() => {
    setEvents([]);
    setPage(1);
    setTotalPages(1);
    setRecords(0);
    setDelegatedSessions(undefined as unknown as DelegatedSessions);

    if (delegated_session_id) {
      getDelegatedSessionData(delegated_session_id);
    }

    fetchEvents(1);
  }, [sessionId, delegated_session_id]);

  const titleMap = {
    delegated_access: 'Acesso Delegado',
    section_client: 'Sessão do Cliente',
    section_adm_user: 'Sessão do Usuário Administrativo',
  };

  const renderDelegatedStatus = (row: DelegatedSessions): ReactNode => {
    if (delegatedSessions) {
      const label =
        delegatedAccessLabels[delegatedSessions?.status] ?? row?.status;
      const detail = expiredDetail(row);

      if (!detail) {
        return label;
      }

      return (
        <>
          {label}
          <Tooltip title={detail.tooltip} placement="top">
            <SubtitleCell>{detail.label}</SubtitleCell>
          </Tooltip>
        </>
      );
    }
  };

  const { openSnackbar } = useContext(GlobalContext);
  const { flag } = useParams();

  const endDelegatedAccess = useCallback(
    async (id: number) => {
      setEndingSession(true);
      try {
        const { data, error } = await endDelegatedSession(Number(id));

        if (error) throw new Error(error.message);
        if (!data) throw new Error('Algo deu errado, tente novamente.');

        setDelegatedSessions(data);
        setOpenEndSessionDialog(false);
        onSessionEnded?.();
      } catch (error) {
        handleSnackbar(getErrorMessage(error), true);
      } finally {
        setEndingSession(false);
      }
    },
    [handleSnackbar, getErrorMessage, onSessionEnded]
  );

  return (
    <Container>
      {!flag?.includes('administrative') && (
        <TopContainer>
          <IconButton onClick={() => onClose()}>{IconCloseMS}</IconButton>
        </TopContainer>
      )}
      <StyledTitle>
        {source ? titleMap[source] : 'Sessão do cliente'}
      </StyledTitle>

      <ContainerInfo>
        <Span>
          <Label>Status:</Label>{' '}
          {delegatedSessions &&
          delegatedSessions.status === 'active' &&
          userRole === 'supervisor' ? (
            <>
              <div>
                <RoundedGreen />
                Ativa
              </div>
              <Button
                variant="outlined"
                rounded
                isRedButton
                onClick={() => setOpenEndSessionDialog(true)}
              >
                Encerrar sessão
              </Button>
            </>
          ) : delegatedSessions?.status ? (
            <>
              {renderDelegatedStatus(delegatedSessions)} {IconArrowForwardMS}{' '}
              {formatDate(
                delegatedSessions.status === 'rejected'
                  ? delegatedSessions.consent_responded_at
                  : delegatedSessions.session_ended_at,
                'absolute'
              )}
            </>
          ) : ended_at ? (
            <>
              Sessão encerrada {IconArrowForwardMS}{' '}
              {ended_at && formatDate(ended_at, 'absolute')}
            </>
          ) : (
            <>
              <RoundedGreen /> Ativa
            </>
          )}
        </Span>

        <Span>
          <Label>Atividade:</Label> {records || 0}
          <ErrorBox>
            <WarningOutlined fontSize="small" /> {error_count || 0} erros
          </ErrorBox>
        </Span>
        <Span>
          <Label>Versão:</Label> {browserVersion || 'Desconhecida'}
        </Span>
        <Span>
          <Label>Sistema operacional:</Label> {os || 'Desconhecido'}
        </Span>
        <Span>
          <Label>Navegador:</Label> {browserName || 'Desconhecido'}
        </Span>
      </ContainerInfo>

      <Divider sx={{ margin: '32px 0' }} />

      <DateTypography>
        {events[0] && formatDateLongMonthLocal(events[0].occurred_at)}
      </DateTypography>
      <DurationTypography>
        {sessionDurationMs !== null &&
          `Duração: ${formatMillisToMinutesSeconds(sessionDurationMs)}`}
      </DurationTypography>

      <Timeline
        source={source}
        events={sessionId ? events : undefined}
        onActivityClick={onActivityClick}
      />
      {sessionId && page < totalPages && (
        <Button variant="text" onClick={() => fetchEvents(page + 1)}>
          Carregar mais
        </Button>
      )}
      {openSnackbar && <Snackbar />}
      <EndSessionConfirmDialog
        open={openEndSessionDialog}
        onClose={() => setOpenEndSessionDialog(false)}
        onConfirm={() => endDelegatedAccess(Number(delegated_session_id))}
        loading={endingSession}
      />
    </Container>
  );
}
